<template>
    <div class="home-form gift-form-wrapper">
        <!-- Amount -->
        <h2>How much Crema Cash <br class="hide-on-desktop">would you like to gift?</h2>
        <div v-if="isUserSignedIn">
            Every time you gift a friend $30 or more, <br class="hide-on-desktop">we 'll reward your generosity with $1 in Crema Cash!
        </div>
        <div v-else>
            Your friend  will receive an email with <br class="hide-on-desktop">instructions to start using their site credit immediately!
        </div>
        <price-selector
                :default-amount-dollar="20"
                :max-amount-cents="multipleRecipients ? 30000 : 100000"
                :amount.sync="amount"
                ref="priceSelector"
                class="margin-top-lg" >
            <template slot="sub-header">
                <h3 v-if="multipleRecipients" class="no-margin">each</h3>
            </template>
        </price-selector>

        <!-- Individual or Team -->
        <div class="multiple-toggle-wrapper">
            <div class="multiple-toggle" @click="toggleMultipleRecipients(false)" :class="!multipleRecipients ? 'multiple-toggle-selected' : ''">Give to an Individual</div>
            or
            <div class="multiple-toggle" @click="toggleMultipleRecipients(true)" :class="multipleRecipients ? 'multiple-toggle-selected' : ''">Give to a Team</div>
        </div>

        <!-- Recipient details -->
        <form>
            <fieldset>
                <recipient-details
                        v-for="(key, i) in recipientKeys"
                        v-show="multipleRecipients || i === 0"
                        :multiple-recipients="multipleRecipients"
                        :removable="numRecipients > 2"
                        :recipient-key="key"
                        :recipient-order="i"
                        :user-email="userEmail"
                        :key="key"
                        v-on:remove="removeRecipient"
                        ref="recipients"
                />

                <div v-if="showAddRecipientBtn" class="add-recipient" @click="addRecipient()">+</div>
            </fieldset>

            <!-- Message -->
            <fieldset>
                <div class="input-group textarea-wrapper">
                    <textarea name="gift-message" id="gift-message" rows="5" placeholder="Custom Message (Optional)" v-model="message" maxlength="1000"></textarea>
                </div>
                <p class="font-sm">*We include this message when we send them instructions on how to redeem your gift.</p>
            </fieldset>

            <fieldset class="mt-8 mb-6">
                <p class="mb-0 mx-auto max-w-sm">What date should the gift notification email<span v-if="multipleRecipients">s</span> arrive and totally make your recipient's day?</p>
                <div class="flex items-center justify-center">
                    <input type="checkbox" id="notificationToggle" v-model="notifyNow">
                    <label for="notificationToggle">Today</label>
                </div>
                <div v-if="!notifyNow" class="mb-4">
                    <p class="error-msg-center mb-2">{{ notificationError }}</p>
                    <date-picker v-model="notificationScheduledAt" :min-date="tomorrow" :max-date="threeMonthsLater"></date-picker>
                </div>
            </fieldset>
        </form>
    </div>
</template>

<script>
    import RecipientDetails from './gift-recipient-detail.vue'
    import PriceSelector from '../../common/price-selector.vue'
    import DatePicker from 'v-calendar/lib/components/date-picker.umd'

    const MAX_RECIPIENTS = 10;

    export default {
        components: {
            PriceSelector,
            RecipientDetails,
            DatePicker
        },
        props: {
            isUserSignedIn: Boolean,
            userEmail: String
        },
        data: function () {
            return {
                multipleRecipients: false,
                amount: 0,
                message: '',
                recipientKeys: [0],
                keyCount: 0,
                notifyNow: false,
                notificationScheduledAt: null,
                notificationError: null
            }
        },
        methods: {
            toggleMultipleRecipients(multiple) {
                if (multiple === this.multipleRecipients) {
                    return;
                }

                if (multiple && this.recipientKeys.length === 1) {
                    this.addRecipient();
                }

                this.multipleRecipients = multiple;
            },
            addRecipient() {
                this.recipientKeys.push(++this.keyCount);
            },
            removeRecipient(recipientKey) {
                if (this.numRecipients > 2) {
                    this.recipientKeys = this.recipientKeys.filter(r => r !== recipientKey)
                }
            },
            validate() {
                if (!this.$refs.priceSelector.validate()) {
                    return;
                }

                const recipientDetailComponents = this.getRecipientDetailComponents();
                if (!this.areRecipientDetailsValid(recipientDetailComponents)) {
                    return;
                }

                const giftData = {
                    amountEach: this.amount,
                    recipients: recipientDetailComponents.map(rdc => rdc.getRecipientDetail()),
                    message: this.message.trim(),
                    notificationScheduledAt: this.removeHoursAndSeconds(this.notificationScheduledAt)
                };

                this.$emit('validated', giftData);
            },
            removeHoursAndSeconds(date) {
                if (!date) {
                    return null;
                }

                return new Date(this.notificationScheduledAt.getFullYear(), this.notificationScheduledAt.getMonth(), this.notificationScheduledAt.getDate());
            },
            getRecipientDetailComponents() {
                const recipientDetailComponents = [];

                if (this.multipleRecipients) {
                    this.$refs.recipients.forEach(r => {
                        recipientDetailComponents.push(r);
                    });
                } else {
                    recipientDetailComponents.push(this.$refs.recipients[0]);
                }
                return recipientDetailComponents;
            },
            areRecipientDetailsValid(components) {
                let areValid = true;

                components.forEach(rdc => {
                    rdc.showValidationErrors = true;
                    if (rdc.$v.$invalid) areValid = false;
                });


                this.notificationError = null;
                if (!this.notifyNow && !this.notificationScheduledAt) {
                    areValid = false;
                    this.notificationError = 'Please select the date.'
                }

                return areValid
            }
        },
        computed: {
            numRecipients() {
              return this.multipleRecipients ? this.recipientKeys.length : 1;
            },
            showAddRecipientBtn() {
                return this.multipleRecipients && this.numRecipients < MAX_RECIPIENTS
            },
            tomorrow() {
                const now = new Date();
                return new Date(now.getFullYear(), now.getMonth(), now.getDate()+1);
            },
            threeMonthsLater() {
                const now = new Date();
                return new Date(now.getFullYear(), now.getMonth()+2, now.getDate());
            }
        },
        watch: {
            numRecipients: function() {
                this.$emit('update:numRecipients', this.numRecipients);
            },
            amount: function() {
                this.$emit('update:amount', this.amount);
            },
            notifyNow: function() {
                this.notificationScheduledAt = undefined;
            }
        },
        mounted() {
        }
    }
</script>

<style lang="scss" scoped>
    @import "../../../styles/variables_home";
    @import "../../../styles/common_home";
    @import "../../../styles/form_home.scss";

    .gift-form-wrapper {
        form {
            max-width: 400px;
            margin: auto;
            textarea {
                width: 100%;
                margin: 0 1rem;
                padding: 6px 8px !important;
            }
        }
    }
    .multiple-toggle-wrapper {
        @extend .flex-centered;
        margin: auto;
    }
    .multiple-toggle {
        @extend .flex-centered;
        font-size: 20px;
        width: 100px;
        height: 100px;
        margin: 0 2rem;
        border-radius: 50%;
        border: solid 2px $color-primary;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
        &:hover {
            background-color: rgba($color-primary, .1);
        }
    }
    .multiple-toggle-selected {
        background-color: $color-primary;
        color: $white;
        &:hover {
            background-color: $color-primary;
        }
    }
    .add-recipient {
        @extend .flex-centered;
        width: 50px;
        height: 50px;
        margin: 1rem auto;
        border-radius: 50%;
        color: $white;
        font-size: 40px;
        background-color: $color-primary;
        cursor: pointer;
        &:hover {
            background-color: darken($color-primary, 15%);
        }
    }
    .error-msg-center {
        @extend .error-msg;
        text-align: center !important;
    }

    @media only screen and (max-width: $max-mobile-width) {
        .font-sm {
            font-size: 10px;
        }
    }
</style>
